import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import { SortableContext, useSortable, verticalListSortingStrategy, horizontalListSortingStrategy, arrayMove } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Plus, Trash2, ExternalLink, Search, ArrowUpRight, GripVertical, Pencil,
  Settings2, Eye, EyeOff, X, MoreHorizontal, CheckCircle2, Check, Sparkles, ListChecks, FolderOpen,
} from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

// Iter 43 — Project Onboarding table refresh. Owns everything specific to the
// onboarding folder view: dynamic column defs (system + custom), inline cell
// editing, drag-reorder (Admin/Dept-Admin only), row-edit dialog with
// Delete + Push-to-Content, editable column headers, and "+ Add column".

const STATUS_TEXT_CLS = {
  slate:   "text-slate-700",
  amber:   "text-amber-700",
  sky:     "text-sky-700",
  emerald: "text-emerald-700",
  rose:    "text-rose-700",
  violet:  "text-violet-700",
  indigo:  "text-purple-700",
};

// Iter 44.5 — pill classes for the redesigned Status cell. Soft coloured
// background + matching border + text tone so the sheet stays readable
// while giving each state a clear silhouette.
const STATUS_PILL_CLS = {
  slate:   "bg-slate-100  text-slate-700   border-slate-200",
  amber:   "bg-amber-50   text-amber-700   border-amber-200",
  sky:     "bg-sky-50     text-sky-700     border-sky-200",
  emerald: "bg-emerald-50 text-emerald-700 border-emerald-200",
  rose:    "bg-rose-50    text-rose-700    border-rose-200",
  violet:  "bg-violet-50  text-violet-700  border-violet-200",
  indigo:  "bg-purple-50  text-purple-700  border-purple-200",
};

const STATUS_DOT_CLS = {
  slate: "bg-slate-400", amber: "bg-amber-500", sky: "bg-sky-500",
  emerald: "bg-emerald-500", rose: "bg-rose-500", violet: "bg-violet-500",
  indigo: "bg-purple-500",
};

// -------- Cell primitives --------
function TextCell({ value, onSave, editable, testId, secret = false }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(value ?? "");
  const [reveal, setReveal] = useState(false);
  useEffect(() => setVal(value ?? ""), [value]);
  const commit = () => { setEditing(false); if ((val || "") !== (value || "")) onSave(val); };
  if (editing) {
    return (
      <Input
        autoFocus
        type={secret && !reveal ? "password" : "text"}
        value={val}
        onChange={(e) => setVal(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); if (e.key === "Escape") { setVal(value ?? ""); setEditing(false); } }}
        className="h-7 text-xs px-2"
        data-testid={`${testId}-input`}
        {...(secret ? { autoComplete: "new-password", name: `pluto-seo-secret-${testId}`, "data-lpignore": "true", "data-1p-ignore": true, "data-form-type": "other" } : {})}
      />
    );
  }
  const display = value ? (secret && !reveal ? "•".repeat(Math.min(10, (value || "").length || 6)) : value) : <span className="text-slate-300 italic">—</span>;

  return (
    <div className="flex items-center gap-1 group">
      <span
        onClick={() => editable && setEditing(true)}
        className={`min-h-[24px] flex-1 px-1.5 py-0.5 rounded text-xs ${editable ? "cursor-pointer hover:bg-purple-50 hover:ring-1 hover:ring-purple-200" : ""} truncate`}
        title={editable ? "Click to edit" : ""}
        data-testid={testId}
      >{display}</span>
      {secret && value && (
        <button onClick={() => setReveal((r) => !r)} className="text-slate-400 hover:text-purple-600 opacity-0 group-hover:opacity-100" data-testid={`${testId}-reveal`}>
          {reveal ? <EyeOff className="h-3 w-3" /> : <Eye className="h-3 w-3" />}
        </button>
      )}
    </div>
  );
}

function DropdownCell({ value, options, onSave, editable, testId, canAddOptions, onAddOption }) {
  const opt = options.find((o) => o.key === value);
  const [adding, setAdding] = useState(false);
  const [newVal, setNewVal] = useState("");
  const commit = async () => {
    if (!newVal.trim()) { setAdding(false); return; }
    try { await onAddOption(newVal.trim()); toast.success("Option added"); setNewVal(""); setAdding(false); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  if (!editable) return <span className="text-xs">{opt?.label || <span className="text-slate-300 italic">—</span>}</span>;
  return (
    <Select value={value || ""} onValueChange={onSave}>
      <SelectTrigger className="h-7 text-xs px-2 border-slate-200 hover:border-purple-300" data-testid={testId}>
        <SelectValue placeholder="—">{opt?.label || <span className="text-slate-300 italic">—</span>}</SelectValue>
      </SelectTrigger>
      <SelectContent>
        {options.map((o) => (<SelectItem key={o.key} value={o.key} data-testid={`${testId}-opt-${o.key}`}>{o.label}</SelectItem>))}
        {canAddOptions && (
          <div className="border-t border-slate-100 mt-1 pt-1 px-2 pb-1">
            {adding ? (
              <div className="flex items-center gap-1">
                <Input autoFocus value={newVal} onChange={(e) => setNewVal(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") commit(); if (e.key === "Escape") { setAdding(false); setNewVal(""); } }}
                  className="h-6 text-[11px] px-2" placeholder="New option name" data-testid={`${testId}-newopt-input`}
                />
                <button onClick={commit} className="text-purple-600 hover:text-purple-800 p-0.5" title="Save" data-testid={`${testId}-newopt-save`}>
                  <Plus className="h-3 w-3" />
                </button>
              </div>
            ) : (
              <button onClick={(e) => { e.preventDefault(); e.stopPropagation(); setAdding(true); }} className="w-full text-left text-[11px] text-purple-600 hover:bg-purple-50 rounded px-2 py-1 flex items-center gap-1" data-testid={`${testId}-newopt-btn`}>
                <Plus className="h-3 w-3" /> Add new option
              </button>
            )}
          </div>
        )}
      </SelectContent>
    </Select>
  );
}

function LinksCell({ value, onSave, editable, testId }) {
  // Iter 45.7 — multi-link cell for the "links" column type.
  //   • Value is stored as a newline-separated string so nothing else in the
  //     backend has to change (still fits `extra.{key}`).
  //   • Display: horizontal chip strip. Each chip is a clickable pill that
  //     opens the URL in a new tab. A "+N" chip appears when a row has more
  //     links than we can render inline.
  //   • Edit:   a textarea (one URL per line) shown in a Popover to keep the
  //     row height tight. Ctrl/Cmd + Enter to save, Esc to cancel.
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value ?? "");
  useEffect(() => setDraft(value ?? ""), [value]);

  const links = String(value ?? "")
    .split(/\r?\n|,\s*/)              // accept newline OR comma-separated
    .map((s) => s.trim())
    .filter(Boolean);

  const commit = () => {
    // Normalise to newline-separated, trimmed, no blanks.
    const cleaned = String(draft ?? "")
      .split(/\r?\n/)
      .map((s) => s.trim())
      .filter(Boolean)
      .join("\n");
    setEditing(false);
    if (cleaned !== (value ?? "")) onSave(cleaned);
  };

  const cancel = () => { setDraft(value ?? ""); setEditing(false); };

  const chipHref = (raw) => (raw.startsWith("http") ? raw : `https://${raw}`);
  const chipLabel = (raw) => {
    try {
      const u = new URL(raw.startsWith("http") ? raw : `https://${raw}`);
      const host = u.hostname.replace(/^www\./, "");
      const path = u.pathname && u.pathname !== "/" ? u.pathname : "";
      return path ? `${host}${path}` : host;
    } catch { return raw; }
  };

  const MAX_INLINE = 2;
  const visible = links.slice(0, MAX_INLINE);
  const overflow = links.length - visible.length;

  return (
    <Popover open={editing} onOpenChange={(o) => (o ? setEditing(!!editable) : cancel())}>
      <PopoverTrigger asChild>
        <div
          className={`min-h-[24px] flex items-center gap-1 flex-wrap px-1.5 py-0.5 rounded text-xs ${editable ? "cursor-pointer hover:bg-purple-50 hover:ring-1 hover:ring-purple-200" : ""}`}
          data-testid={testId}
          title={editable ? "Click to edit links" : ""}
        >
          {links.length === 0 && <span className="text-slate-300 italic">—</span>}
          {visible.map((raw, i) => (
            <a
              key={i}
              href={chipHref(raw)}
              target="_blank"
              rel="noreferrer"
              onClick={(e) => e.stopPropagation()}
              className="inline-flex items-center gap-1 max-w-[180px] text-[11px] px-1.5 py-0.5 rounded-full border border-purple-200 bg-purple-50 text-purple-700 hover:bg-purple-100 truncate"
              data-testid={`${testId}-chip-${i}`}
            >
              <ExternalLink className="h-2.5 w-2.5 shrink-0" />
              <span className="truncate">{chipLabel(raw)}</span>
            </a>
          ))}
          {overflow > 0 && (
            <span className="text-[10px] px-1.5 py-0.5 rounded-full border border-slate-200 bg-slate-50 text-slate-600" data-testid={`${testId}-overflow`}>
              +{overflow}
            </span>
          )}
        </div>
      </PopoverTrigger>
      {editable && (
        <PopoverContent className="w-80" align="start" sideOffset={4} data-testid={`${testId}-popover`}>
          <div className="space-y-2">
            <div className="text-[11px] font-semibold text-slate-600 flex items-center gap-1.5">
              <ExternalLink className="h-3 w-3 text-purple-500" /> Links · one per line
            </div>
            <textarea
              autoFocus
              rows={5}
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) { e.preventDefault(); commit(); }
                if (e.key === "Escape") { e.preventDefault(); cancel(); }
              }}
              className="w-full text-xs font-mono border border-slate-200 rounded px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-purple-300"
              placeholder={"https://example.com\nhttps://another.com"}
              data-testid={`${testId}-textarea`}
            />
            <div className="flex items-center justify-end gap-2">
              <Button variant="outline" size="sm" onClick={cancel} data-testid={`${testId}-cancel`}>Cancel</Button>
              <Button size="sm" onClick={commit} className="bg-purple-600 hover:bg-purple-700 text-white h-7 text-xs" data-testid={`${testId}-save`}>
                Save
              </Button>
            </div>
          </div>
        </PopoverContent>
      )}
    </Popover>
  );
}


// Iter 45.25 — derive a website URL from the project name (mirrors the
// backend `_derive_site_url` helper). "Karnes Interior" → https://karnesinterior.com
function deriveProjectUrl(name) {
  const slug = (name || "").toLowerCase().replace(/\s+/g, "").replace(/[^a-z0-9.\-]/g, "");
  if (!slug) return "";
  return `https://${slug.includes(".") ? slug : slug + ".com"}`;
}

// Iter 45.26 — Project Name cell rework per user correction:
//   • Project NAME links to the project spreadsheet (once pushed).
//   • The auto-derived project URL renders UNDERNEATH the name as a
//     small external link (same layout as the Projects tab).
//   • Hovering reveals a pencil for inline rename (pre- and post-push);
//     renaming a pushed row also renames the linked Content project
//     server-side (see PATCH /seo/items).
function ProjectNameCell({ item, editable, onSave, testId }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(item.project_name ?? "");
  useEffect(() => setVal(item.project_name ?? ""), [item.project_name]);
  const commit = () => {
    setEditing(false);
    const v = (val || "").trim();
    if (v && v !== (item.project_name || "")) onSave(v);
    else setVal(item.project_name ?? "");
  };
  const url = deriveProjectUrl(item.project_name);
  return (
    <div className="flex items-start gap-2 group" data-testid={testId}>
      {item.pushed_project_id ? (
        <Link
          to={`/projects/${item.pushed_project_id}`}
          title="Open project spreadsheet"
          data-testid={`${testId}-icon-link`}
          className="shrink-0 mt-0.5"
        >
          <FolderOpen className="h-4 w-4 text-purple-500 hover:text-purple-700 transition" />
        </Link>
      ) : (
        <FolderOpen className="h-4 w-4 shrink-0 mt-0.5 text-purple-500" />
      )}
      <div className="min-w-0">
        <div className="flex items-center gap-1">
          {editing ? (
            <Input
              autoFocus
              value={val}
              onChange={(e) => setVal(e.target.value)}
              onBlur={commit}
              onKeyDown={(e) => {
                if (e.key === "Enter") e.currentTarget.blur();
                if (e.key === "Escape") { setVal(item.project_name ?? ""); setEditing(false); }
              }}
              className="h-7 text-xs px-2"
              data-testid={`${testId}-input`}
            />
          ) : item.pushed_project_id ? (
            <Link
              to={`/projects/${item.pushed_project_id}`}
              className="font-medium text-slate-800 hover:text-purple-600 truncate block"
              title="Open project spreadsheet"
              data-testid={`${testId}-sheet-link`}
            >
              {item.project_name}
            </Link>
          ) : (
            <span
              onClick={() => editable && setEditing(true)}
              className={`font-medium text-slate-800 truncate block ${editable ? "cursor-pointer hover:text-purple-600" : ""}`}
              title={editable ? "Click to rename" : ""}
              data-testid={`${testId}-name`}
            >
              {item.project_name || <span className="text-slate-300 italic text-xs">—</span>}
            </span>
          )}
          {editable && !editing && (
            <button
              onClick={() => setEditing(true)}
              className="opacity-0 group-hover:opacity-100 text-slate-400 hover:text-purple-600 shrink-0 transition"
              title="Rename project"
              data-testid={`${testId}-edit`}
            >
              <Pencil className="h-3 w-3" />
            </button>
          )}
        </div>
        {url && (
          <a
            href={url}
            target="_blank"
            rel="noreferrer"
            className="text-[11px] text-slate-500 hover:text-purple-600 truncate flex items-center gap-1 mt-0.5"
            data-testid={`${testId}-url-link`}
          >
            <ExternalLink className="h-3 w-3" /> {url}
          </a>
        )}
      </div>
    </div>
  );
}

function StatusCell({ value, options, onSave, editable, testId }) {
  // Iter 44.5 — status renders as a rounded pill with soft coloured bg +
  // dot for at-a-glance state indication. Editable variants keep the
  // Select trigger but style the trigger surface to match the pill.
  // Iter 45.9 — options may carry an `icon: "check"` hint to swap the dot
  // for a ✓ glyph (used by "Pushed" so it reads as "done" at a glance).
  const opt = options.find((o) => o.key === value) || options[0];
  const pillCls = STATUS_PILL_CLS[opt?.color] || STATUS_PILL_CLS.slate;
  const dotCls = STATUS_DOT_CLS[opt?.color] || STATUS_DOT_CLS.slate;
  const StatusGlyph = ({ o, dCls }) => (
    o?.icon === "check"
      ? <Check className="h-3 w-3 shrink-0" strokeWidth={3} />
      : <span className={`h-1.5 w-1.5 rounded-full ${dCls}`} />
  );
  const Pill = () => (
    <span className={`inline-flex items-center gap-1.5 text-[11px] font-semibold px-2.5 py-1 rounded-full border ${pillCls}`}>
      <StatusGlyph o={opt} dCls={dotCls} />
      {opt?.label || "—"}
    </span>
  );
  if (!editable) return <Pill />;
  return (
    <Select value={value || ""} onValueChange={onSave}>
      <SelectTrigger
        className={`h-auto py-0 px-0 text-xs border-0 shadow-none bg-transparent hover:bg-transparent focus:ring-0 w-auto min-w-0 [&_svg]:hidden`}
        data-testid={testId}
      >
        <SelectValue><Pill /></SelectValue>
      </SelectTrigger>
      <SelectContent>
        {options.map((o) => {
          const p = STATUS_PILL_CLS[o.color] || STATUS_PILL_CLS.slate;
          const d = STATUS_DOT_CLS[o.color] || STATUS_DOT_CLS.slate;
          return (
            <SelectItem key={o.key} value={o.key} data-testid={`${testId}-opt-${o.key}`}>
              <span className={`inline-flex items-center gap-1.5 text-[11px] font-semibold px-2.5 py-0.5 rounded-full border ${p}`}>
                <StatusGlyph o={o} dCls={d} />
                {o.label}
              </span>
            </SelectItem>
          );
        })}
      </SelectContent>
    </Select>
  );
}

function LaunchDateCell({ value, onSave, editable, testId }) {  // Iter 44.5 — Launch Date is fully editable inline; "Auto" chip removed
  // per user ask. Backend still auto-fills on status→live when the field
  // is empty, but the poster can now manually override / clear it.
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(value || "");
  useEffect(() => setVal(value || ""), [value]);
  const commit = () => {
    setEditing(false);
    if ((val || "") !== (value || "")) onSave(val || "");
  };
  if (editing && editable) {
    return (
      <Input
        autoFocus
        type="date"
        value={val}
        onChange={(e) => setVal(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === "Enter") e.currentTarget.blur();
          if (e.key === "Escape") { setVal(value || ""); setEditing(false); }
        }}
        className="h-7 text-xs px-2"
        data-testid={`${testId}-input`}
      />
    );
  }
  return (
    <span
      onClick={() => editable && setEditing(true)}
      className={`inline-flex items-center min-h-[24px] px-1.5 py-0.5 rounded text-xs text-slate-700 ${editable ? "cursor-pointer hover:bg-purple-50 hover:ring-1 hover:ring-purple-200" : ""}`}
      title={editable ? "Click to edit launch date" : ""}
      data-testid={testId}
    >
      {value || <span className="text-slate-300 italic">—</span>}
    </span>
  );
}

// -------- Editable column header --------
function ColumnHeader({ col, canManage, onRename, onDelete }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(col.label);
  useEffect(() => setVal(col.label), [col.label]);
  const commit = () => {
    setEditing(false);
    if (val.trim() && val.trim() !== col.label) onRename(col.key, val.trim());
    else setVal(col.label);
  };
  if (editing) {
    return (
      <Input autoFocus value={val}
        onChange={(e) => setVal(e.target.value)} onBlur={commit}
        onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); if (e.key === "Escape") { setVal(col.label); setEditing(false); } }}
        className="h-6 text-[11px] px-1.5 font-semibold uppercase"
        data-testid={`col-header-input-${col.key}`}
      />
    );
  }
  return (
    <div className="flex items-center gap-1 group" data-testid={`col-header-${col.key}`}>
      <span>{col.label}</span>
      {canManage && (
        <>
          <button onClick={() => setEditing(true)} title="Rename column" className="opacity-0 group-hover:opacity-100 text-slate-400 hover:text-purple-600" data-testid={`col-rename-${col.key}`}>
            <Pencil className="h-2.5 w-2.5" />
          </button>
          {!col.system && (
            <button onClick={() => onDelete(col.key)} title="Delete column" className="opacity-0 group-hover:opacity-100 text-slate-400 hover:text-red-600" data-testid={`col-delete-${col.key}`}>
              <X className="h-3 w-3" />
            </button>
          )}
        </>
      )}
    </div>
  );
}

// -------- Row-edit dialog --------
function RowEditDialog({ item, folder, open, onClose, onSaved, onDelete, onPush, isSystemPushEnabled }) {
  const [draft, setDraft] = useState(item || {});
  const [saving, setSaving] = useState(false);
  useEffect(() => { setDraft(item || {}); }, [item, open]);

  if (!item) return null;
  const cols = folder.columns || [];
  const hostedOptions = folder.hosted_on_options || [];
  const statusOptions = folder.status_options || [];

  const setField = (key, val, isExtra = false) => {
    if (isExtra) setDraft((d) => ({ ...d, extra: { ...(d.extra || {}), [key]: val } }));
    else setDraft((d) => ({ ...d, [key]: val }));
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {};
      for (const c of cols) {
        if (c.system) {
          if (draft[c.key] !== item[c.key]) payload[c.key] = draft[c.key] ?? "";
        } else {
          const cur = (item.extra || {})[c.key];
          const nxt = (draft.extra || {})[c.key];
          if (nxt !== cur) payload.extra = { ...(payload.extra || {}), [c.key]: nxt ?? "" };
        }
      }
      if (Object.keys(payload).length === 0) { onClose(); setSaving(false); return; }
      await onSaved(item.id, payload);
      onClose();
    } catch (e) { toast.error(formatApiError(e)); } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="row-edit-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-purple-800">
            <Settings2 className="h-4 w-4" /> Edit row · {draft.project_name || "Untitled"}
          </DialogTitle>
          <DialogDescription>All column values are editable here. Inline cell editing still works too.</DialogDescription>
        </DialogHeader>

        <div className="space-y-3 py-2">
          {cols.filter((c) => c.key !== "launch_date").map((c) => {
            const val = c.system ? draft[c.key] : (draft.extra || {})[c.key];
            return (
              <div key={c.key} className="grid grid-cols-3 items-center gap-3">
                <Label className="text-xs text-slate-600">{c.label}</Label>
                <div className="col-span-2">
                  {c.type === "status" ? (
                    <Select value={val || ""} onValueChange={(v) => setField(c.key, v)}>
                      <SelectTrigger className="h-8 text-xs" data-testid={`dialog-${c.key}`}><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {statusOptions.map((o) => <SelectItem key={o.key} value={o.key}>{o.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  ) : c.type === "dropdown" ? (
                    <Select value={val || ""} onValueChange={(v) => setField(c.key, v, !c.system)}>
                      <SelectTrigger className="h-8 text-xs" data-testid={`dialog-${c.key}`}><SelectValue placeholder="—" /></SelectTrigger>
                      <SelectContent>
                        {(c.key === "hosted_on" ? hostedOptions : (c.options || [])).map((o) => <SelectItem key={o.key} value={o.key}>{o.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  ) : c.type === "links" ? (
                    <textarea
                      rows={3}
                      value={val || ""}
                      onChange={(e) => setField(c.key, e.target.value, !c.system)}
                      placeholder={"https://example.com\nhttps://another.com"}
                      className="w-full text-xs font-mono border border-slate-200 rounded px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-purple-300"
                      data-testid={`dialog-${c.key}`}
                    />
                  ) : (
                    <Input
                      value={val || ""}
                      onChange={(e) => setField(c.key, e.target.value, !c.system)}
                      type={c.key === "password" ? "password" : "text"}
                      className="h-8 text-xs"
                      data-testid={`dialog-${c.key}`}
                    />
                  )}
                </div>
              </div>
            );
          })}
          {/* Launch Date — fully editable, no "Auto" tag (iter 44.5). Backend
              still fills it in when status flips to Live if left blank. */}
          <div className="grid grid-cols-3 items-center gap-3">
            <Label className="text-xs text-slate-600">Launch Date</Label>
            <div className="col-span-2">
              <Input type="date" value={draft.launch_date || draft.live_date || ""} onChange={(e) => setField("launch_date", e.target.value)} className="h-8 text-xs w-44" data-testid="dialog-launch_date" />
            </div>
          </div>
        </div>

        <DialogFooter className="border-t pt-3 flex-wrap gap-2">
          <div className="mr-auto flex items-center gap-2">
            <Button variant="outline" onClick={() => onDelete(item.id)} className="h-8 text-xs text-red-600 border-red-200 hover:bg-red-50" data-testid="dialog-delete-btn">
              <Trash2 className="h-3.5 w-3.5 mr-1" /> Delete row
            </Button>
            {isSystemPushEnabled && !item.pushed_to_content && (
              <Button onClick={() => onPush(item.id)} className="h-8 text-xs bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="dialog-push-btn">
                <ArrowUpRight className="h-3.5 w-3.5 mr-1" /> Push to Content Team
              </Button>
            )}
            {item.pushed_to_content && (
              <span className="text-[10px] bg-emerald-50 text-emerald-700 border border-emerald-200 px-2 py-1 rounded font-semibold" data-testid="dialog-pushed-badge">
                ✓ Pushed to Content
              </span>
            )}
          </div>
          <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button onClick={save} disabled={saving} className="bg-purple-600 hover:bg-purple-700" data-testid="dialog-save-btn">Save changes</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// -------- Add-column dialog --------
function AddColumnDialog({ open, onClose, onAdd }) {
  const [label, setLabel] = useState("");
  const [type, setType] = useState("text");
  const [options, setOptions] = useState("");
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (!open) { setLabel(""); setType("text"); setOptions(""); } }, [open]);

  const save = async () => {
    if (!label.trim()) return;
    setSaving(true);
    try {
      const payload = { label: label.trim(), type };
      if (type === "dropdown") payload.options = options.split("\n").map((s) => s.trim()).filter(Boolean);
      await onAdd(payload);
      onClose();
    } catch (e) { toast.error(formatApiError(e)); } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="add-col-dialog">
        <DialogHeader>
          <DialogTitle>Add column</DialogTitle>
          <DialogDescription>Create a text, links or dropdown column. Only Admin / SEO Dept Admin can add/rename/delete columns.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div>
            <Label className="text-xs">Column name</Label>
            <Input value={label} onChange={(e) => setLabel(e.target.value)} className="h-8 mt-1" placeholder="e.g. Priority" data-testid="add-col-label" />
          </div>
          <div>
            <Label className="text-xs">Type</Label>
            <Select value={type} onValueChange={setType}>
              <SelectTrigger className="h-8 mt-1" data-testid="add-col-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="text">Text (free-form)</SelectItem>
                <SelectItem value="links">Links (multiple URLs)</SelectItem>
                <SelectItem value="dropdown">Dropdown (fixed choices)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {type === "dropdown" && (
            <div>
              <Label className="text-xs">Options — one per line</Label>
              <textarea rows={4} value={options} onChange={(e) => setOptions(e.target.value)}
                className="mt-1 w-full text-xs border border-slate-200 rounded px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-purple-300"
                placeholder={"High\nMedium\nLow"} data-testid="add-col-options" />
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button onClick={save} disabled={saving || !label.trim()} className="bg-purple-600 hover:bg-purple-700" data-testid="add-col-save">Add column</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// -------- Sortable row wrapper --------
function SortableRow({ id, disabled, children, testId }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id, disabled });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1 };
  return (
    <tr ref={setNodeRef} style={style} data-testid={testId} className={`group transition-colors ${isDragging ? "bg-purple-50" : ""}`}>
      {typeof children === "function" ? children({ listeners, attributes }) : children}
    </tr>
  );
}

// -------- Sortable header cell (Iter 45.10) --------
// Wraps a `<th>` in `useSortable` so its column can be dragged horizontally
// to change order. Rename / delete controls inside the header stay clickable
// because the drag handle is a separate <button> that owns the listeners.
function SortableTh({ colKey, disabled, className, style, children }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: colKey, disabled });
  const composedStyle = {
    ...(style || {}),
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };
  return (
    <th
      ref={setNodeRef}
      className={`${className || ""} relative group ${isDragging ? "bg-purple-50" : ""}`}
      style={composedStyle}
      data-testid={`col-th-${colKey}`}
    >
      {!disabled && (
        <button
          {...attributes}
          {...listeners}
          type="button"
          className="absolute -left-1 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 text-slate-400 hover:text-purple-600 cursor-grab active:cursor-grabbing p-0.5 z-10"
          title="Drag to reorder column"
          data-testid={`col-drag-${colKey}`}
          onClick={(e) => e.preventDefault()}
        >
          <GripVertical className="h-3 w-3" />
        </button>
      )}
      {children}
    </th>
  );
}

// -------- Main table --------
export default function OnboardingTable({ folder, items, setItems, setFolder, reload }) {
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [editingRow, setEditingRow] = useState(null);
  const [addColOpen, setAddColOpen] = useState(false);
  const [pushed, setPushed] = useState({});

  const canEdit = folder.can_edit_items;
  const canManage = folder.can_manage; // Admin / Super Admin / SEO Dept Admin only
  const cols = folder.columns || [];
  const hostedOptions = folder.hosted_on_options || [];
  const statusOptions = folder.status_options || [];

  const filtered = useMemo(() => {
    let list = items;
    if (statusFilter !== "all") list = list.filter((x) => x.status === statusFilter);
    if (q.trim()) {
      const s = q.trim().toLowerCase();
      list = list.filter((x) =>
        (x.project_name || "").toLowerCase().includes(s) ||
        (x.example_site || "").toLowerCase().includes(s) ||
        (x.username || "").toLowerCase().includes(s),
      );
    }
    return list;
  }, [items, q, statusFilter]);

  const patch = async (id, body) => {
    setItems((all) => all.map((x) => (x.id === id ? { ...x, ...body, extra: body.extra ? { ...(x.extra || {}), ...body.extra } : x.extra } : x)));
    try {
      const r = await api.patch(`/seo/items/${id}`, body);
      setItems((all) => all.map((x) => (x.id === id ? r.data : x)));
    } catch (e) { toast.error(formatApiError(e)); reload(); }
  };

  const addRow = async () => {
    try {
      const r = await api.post("/seo/items", { folder_id: folder.id, project_name: "New project" });
      setItems((all) => [...all, r.data]);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const deleteRow = async (id) => {
    if (!window.confirm("Delete this row?")) return;
    try {
      await api.delete(`/seo/items/${id}`);
      setItems((all) => all.filter((x) => x.id !== id));
      setEditingRow(null);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const push = async (id) => {
    setPushed((s) => ({ ...s, [id]: true }));
    try {
      const r = await api.post(`/seo/items/${id}/push-to-content`);
      toast.success(`Pushed — Content project "${r.data.project.name}" created`);
      // Iter 45.24 — push also flips the row's Status to "Pushed"
      // (backend guarantees the option exists on the folder).
      setItems((all) => all.map((x) => (x.id === id ? { ...x, pushed_to_content: true, pushed_project_id: r.data.project.id, status: "pushed" } : x)));
      setEditingRow(null);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setPushed((s) => ({ ...s, [id]: false })); }
  };

  const renameCol = async (key, label) => {
    try {
      const r = await api.patch(`/seo/folders/${folder.id}/columns/${key}`, { label });
      setFolder(r.data);
      toast.success("Column renamed");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const deleteCol = async (key) => {
    if (!window.confirm(`Delete column? Values in this column will be permanently removed from every row.`)) return;
    try {
      const r = await api.delete(`/seo/folders/${folder.id}/columns/${key}`);
      setFolder(r.data);
      toast.success("Column deleted");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const addCol = async (payload) => {
    const r = await api.post(`/seo/folders/${folder.id}/columns`, payload);
    setFolder(r.data);
    toast.success("Column added");
  };

  // Iter 45.10 — column drag-reorder handler. `project_name` is pinned at
  // index 0 (see backend); we mirror that here by clamping the drop index
  // to 1 so nothing can land in front of it. Optimistic UI + reload-on-error.
  const onColDragEnd = async ({ active, over }) => {
    if (!over || active.id === over.id) return;
    const keys = cols.map((c) => c.key);
    const fromIdx = keys.indexOf(active.id);
    let toIdx = keys.indexOf(over.id);
    if (fromIdx < 0 || toIdx < 0) return;
    toIdx = Math.max(1, toIdx);
    const nextKeys = arrayMove(keys, fromIdx, toIdx);
    const nextCols = nextKeys.map((k) => cols.find((c) => c.key === k)).filter(Boolean);
    setFolder((prev) => ({ ...prev, columns: nextCols }));
    try {
      await api.post(`/seo/folders/${folder.id}/columns/reorder`, {
        ordered_keys: nextKeys.filter((k) => k !== "project_name"),
      });
      toast.success("Column order saved");
    } catch (e) {
      toast.error(formatApiError(e));
      reload();
    }
  };

  const addHostedOption = async (label) => {
    const key = label.toLowerCase().replace(/[^a-z0-9]+/g, "_").slice(0, 30);
    const existing = folder.hosted_on_options || [];
    if (existing.find((o) => o.key === key)) throw new Error("Option with that name already exists");
    const next = [...existing, { key, label }];
    const r = await api.patch(`/seo/folders/${folder.id}`, { hosted_on_options: next });
    setFolder(r.data);
  };

  // -------- Drag reorder --------
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));
  const onDragEnd = async ({ active, over }) => {
    if (!over || active.id === over.id) return;
    const oldIdx = items.findIndex((x) => x.id === active.id);
    const newIdx = items.findIndex((x) => x.id === over.id);
    if (oldIdx < 0 || newIdx < 0) return;
    const reordered = arrayMove(items, oldIdx, newIdx);
    setItems(reordered);
    try {
      await api.post(`/seo/folders/${folder.id}/items/reorder`, { ordered_ids: reordered.map((x) => x.id) });
    } catch (e) { toast.error(formatApiError(e)); reload(); }
  };

  // -------- Cell renderer for a given (col, item) --------
  const renderCell = (col, it) => {
    const isSys = !!col.system;
    const value = isSys ? it[col.key] : (it.extra || {})[col.key];
    const cellEditable = canEdit && (canManage || !it.pushed_to_content);
    const testId = `cell-${col.key}-${it.id}`;
    const save = (v) => isSys ? patch(it.id, { [col.key]: v }) : patch(it.id, { extra: { [col.key]: v } });
    if (col.key === "launch_date") return (
      <LaunchDateCell
        value={it.live_date}
        onSave={(v) => patch(it.id, { launch_date: v })}
        editable={cellEditable}
        testId={testId}
      />
    );
    if (col.type === "status") return <StatusCell value={value} options={statusOptions} onSave={save} editable={cellEditable} testId={testId} />;
    if (col.type === "links") return <LinksCell value={value} onSave={save} editable={cellEditable} testId={testId} />;
    if (col.type === "dropdown") {
      const opts = col.key === "hosted_on" ? hostedOptions : (col.options || []);
      return <DropdownCell value={value} options={opts} onSave={save} editable={cellEditable} testId={testId}
        canAddOptions={canManage && col.key === "hosted_on"} onAddOption={addHostedOption} />;
    }
    // Iter 45.25 — Project Name cell: purple folder icon (links to the
    // pushed project spreadsheet when one exists) + the project name
    // itself links to the auto-derived website URL (name-derived, e.g.
    // "Karnes Interior" → https://karnesinterior.com). Hovering reveals a
    // pencil that opens inline rename; renaming a pushed row also renames
    // the linked project server-side (see PATCH /seo/items).
    if (col.key === "project_name") {
      return (
        <ProjectNameCell item={it} editable={cellEditable} onSave={save} testId={testId} />
      );
    }
    // text (+ external link glyph for URL-ish columns).
    const isUrl = ["logo_link", "example_site", "temporary_url"].includes(col.key);
    return (
      <div className="flex items-center gap-1">
        <TextCell
          value={value}
          onSave={save}
          editable={cellEditable}
          testId={testId}
          secret={col.key === "password"}
        />
        {isUrl && value && (
          <a href={value.startsWith("http") ? value : `https://${value}`} target="_blank" rel="noreferrer" className="text-slate-400 hover:text-purple-600 shrink-0"><ExternalLink className="h-3 w-3" /></a>
        )}
      </div>
    );
  };

  return (
    <>
      {/* Filter row — Iter 45.11 polish: consolidated into a white card with
          soft shadow so it visually anchors above the sheet instead of
          floating in whitespace. Zero functional changes. */}
      <div className="bg-white border border-slate-200 rounded-2xl shadow-sm p-3 flex items-center gap-2 flex-wrap">
        <div className="relative flex-1 min-w-[240px] max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search project, site, username…" className="pl-9 h-9 border-slate-200 focus-visible:ring-purple-200"
            data-testid="seo-search" autoComplete="off" name="pluto-seo-onboarding-search" data-lpignore="true" data-1p-ignore data-form-type="other" />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="h-9 w-44 border-slate-200" data-testid="seo-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            {statusOptions.map((o) => <SelectItem key={o.key} value={o.key}>{o.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <div className="ml-auto flex items-center gap-2">
          <span className="hidden sm:inline-flex items-center gap-1 text-[11px] text-slate-400 px-2">
            <ListChecks className="h-3.5 w-3.5" />
            {filtered.length}{filtered.length !== items.length ? ` / ${items.length}` : ""} row{filtered.length === 1 ? "" : "s"}
          </span>
          {canManage && (
            <Button variant="outline" onClick={() => setAddColOpen(true)} className="h-9 border-slate-200 hover:border-purple-300 hover:bg-purple-50 hover:text-purple-700" data-testid="add-column-btn">
              <Plus className="h-4 w-4 mr-1" /> Add column
            </Button>
          )}
          {canEdit && (
            <Button onClick={addRow} className="bg-gradient-to-r from-purple-600 to-violet-600 hover:from-purple-700 hover:to-violet-700 text-white h-9 shadow-sm" data-testid="add-item-btn">
              <Plus className="h-4 w-4 mr-1" /> Add row
            </Button>
          )}
        </div>
      </div>

      {/* Spreadsheet — Iter 45.11 polish: rounded-2xl shell with shadow, softer
          zebra rows, hover lift, gradient thead, prettier empty state. */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm max-w-full overflow-hidden">
        <div className="overflow-x-auto">
          {/* Iter 45.29 — `border-separate` + `borderSpacing: 0` are
              REQUIRED for the sticky left columns (drag grip + project
              name) to actually stick. Chrome/Safari/Firefox silently
              ignore `position: sticky` on <td>/<th> when the table uses
              the default `border-collapse: collapse` — same fix as
              `.spreadsheet-table` in index.css. Without it the name
              column scrolled away and sliding columns bled through. */}
          <table className="text-sm w-full border-separate" style={{ minWidth: "max-content", borderSpacing: 0 }}>
            <thead className="bg-gradient-to-b from-slate-50 to-white border-b border-slate-200">
              {/* Iter 45.10 — horizontal drag-reorder for columns. `project_name`
                  is disabled (pinned at index 0 to preserve sticky-left) plus
                  the backend also refuses to move it. Sensors are shared with
                  the row-reorder DndContext below — no conflict since the two
                  contexts don't overlap. */}
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={onColDragEnd}
              >
                <SortableContext items={cols.map((c) => c.key)} strategy={horizontalListSortingStrategy}>
                  <tr>
                    {canManage && <th className="w-8 sticky left-0 z-20 bg-gradient-to-b from-slate-50 to-white" />}
                    {cols.map((c) => (
                      <SortableTh
                        key={c.key}
                        colKey={c.key}
                        disabled={c.key === "project_name" || !canManage}
                        className={`px-3 py-3 text-left text-[10px] font-bold uppercase tracking-[0.12em] text-slate-500 ${c.key === "project_name" ? `sticky ${canManage ? "left-8" : "left-0"} z-20 bg-gradient-to-b from-slate-50 to-white min-w-[200px]` : ""}`}
                        style={{ minWidth: c.width || 140 }}
                      >
                        <ColumnHeader col={c} canManage={canManage} onRename={renameCol} onDelete={deleteCol} />
                      </SortableTh>
                    ))}
                    <th className="px-3 py-3 text-center text-[10px] font-bold uppercase tracking-[0.12em] text-slate-500 w-[120px]">Action</th>
                  </tr>
                </SortableContext>
              </DndContext>
            </thead>
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
              <SortableContext items={filtered.map((it) => it.id)} strategy={verticalListSortingStrategy}>
                <tbody data-testid="onboarding-tbody">
                  {filtered.length === 0 && (
                    <tr>
                      <td colSpan={cols.length + (canManage ? 2 : 1)} className="text-center py-16">
                        <div className="flex flex-col items-center gap-3 text-slate-400">
                          <div className="h-14 w-14 rounded-2xl bg-purple-50 border border-purple-100 flex items-center justify-center">
                            <Sparkles className="h-6 w-6 text-purple-400" />
                          </div>
                          <div className="text-sm font-medium text-slate-600">
                            {items.length === 0 ? "No rows yet" : "No rows match this filter"}
                          </div>
                          <div className="text-xs text-slate-400">
                            {items.length === 0
                              ? (canEdit ? "Click 'Add row' to get started." : "Ask your Dept Admin to add rows.")
                              : "Try clearing the search or status filter."}
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                  {filtered.map((it, idx) => (
                    <SortableRow key={it.id} id={it.id} disabled={!canManage} testId={`seo-row-${it.id}`}>
                      {({ listeners, attributes }) => (
                        <>
                          {canManage && (
                            <td
                              className={`w-8 sticky left-0 z-10 cursor-grab active:cursor-grabbing border-b border-slate-100 ${idx % 2 === 0 ? "bg-white" : "bg-slate-50"} group-hover:bg-purple-50`}
                              {...listeners} {...attributes} data-testid={`drag-${it.id}`}
                            >
                              <GripVertical className="h-3.5 w-3.5 text-slate-300 hover:text-purple-500 mx-auto" />
                            </td>
                          )}
                          {cols.map((c) => (
                            <td key={c.key}
                              className={`px-3 py-2.5 align-middle border-b border-slate-100 ${c.key === "project_name" ? `sticky ${canManage ? "left-8" : "left-0"} z-10 ${idx % 2 === 0 ? "bg-white" : "bg-slate-50"} group-hover:bg-purple-50` : ""}`}
                              style={{ paddingLeft: c.key === "project_name" && canManage ? "12px" : undefined }}
                            >
                              {renderCell(c, it)}
                            </td>
                          ))}
                          <td className="px-2 py-2.5 text-center border-b border-slate-100">
                            {/* Iter 45.24 — "Pushed" tag removed from the
                                Action column per user ask. The Status column
                                now shows "Pushed" automatically after a push,
                                so the tag was redundant. */}
                            <div className="flex flex-col items-center gap-1.5">
                              <Button
                                size="sm" variant="outline"
                                onClick={() => setEditingRow(it)}
                                className="h-7 text-[11px] px-2.5 border-slate-200 hover:bg-purple-600 hover:border-purple-600 hover:text-white transition-colors"
                                data-testid={`action-open-${it.id}`}
                              >
                                <MoreHorizontal className="h-3 w-3 mr-1" /> Actions
                              </Button>
                            </div>
                          </td>
                        </>
                      )}
                    </SortableRow>
                  ))}
                </tbody>
              </SortableContext>
            </DndContext>
          </table>
        </div>
      </div>

      <RowEditDialog
        item={editingRow}
        folder={folder}
        open={!!editingRow}
        onClose={() => setEditingRow(null)}
        onSaved={patch}
        onDelete={deleteRow}
        onPush={push}
        isSystemPushEnabled={canManage}
      />

      <AddColumnDialog open={addColOpen} onClose={() => setAddColOpen(false)} onAdd={addCol} />
    </>
  );
}
